import React, { useEffect, useState } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import {
  Box,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Checkbox,
  Chip,
  FormHelperText,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { CircleMinus, RefreshCcw } from "lucide-react";
import { useGetList } from "../../../../hooks/useGetList";
import Swal from "sweetalert2";
import { usePost } from "../../../../hooks/usePost";
import { useUpdate } from "../../../../hooks/useUpdate";

const PurchaseForm = () => {
  const [showTable, setShowTable] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      code: "",
      relatedBranch: "",
      purchaseRequestDate: "",
      requiredDate: "",
      reason: "",
      remark: "",
      relatedSupplier: "",
      relatedProduct: "",
      relatedProductItem: "",
      relatedItemUnit: "",
      qty: "",
      purchasePrice: "",
      expireDate: "",
      addPurchaseCode: false,
      selectedProduct: [],
    },
  });

  const { postData, loading } = usePost(`v2/purchase-request`);
  const { updateData, loading: updateLoading } = useUpdate(`item-unit/price`);

  const {
    fields: selectedProductFields,
    append: appendVoucher,
    remove: removeVoucher,
    update: updateVoucher,
  } = useFieldArray({
    control,
    name: "selectedProducts",
  });

  const { data: branchData, loading: branchLoading } =
    useGetList(`branch/options`);

  const { data: product, loading: productLoading } =
    useGetList(`product-list/options`);

  const { data: suppliers, loading: supplierLoading } =
    useGetList(`supplier/options`);

  const watchProduct = watch("relatedProduct");
  const watchProductItem = watch("relatedProductItem");
  const watchItemUnit = watch("relatedItemUnit");
  const watchPurchasePrice = watch("purchasePrice");
  const watchRelatedBranch = watch("relatedBranch");

  const { data: productItems, loading: productItemLoading } = useGetList(
    `product-item/options?relatedProduct=${watchProduct}`,
  );

  const { data: itemUnits, loading: itemUnitLoading } = useGetList(
    `item-unit/options?relatedItem=${watchProductItem}&relatedBranch=${watchRelatedBranch}`,
  );

  const { data: unitInfo, loading: unitInfoLoading } = useGetList(
    `unit-info/price?relatedUnit=${watchItemUnit}&relatedBranch=${watchRelatedBranch}`,
    watchRelatedBranch && watchItemUnit ? true : false,
  );

  const watchAddPurchaseCode = watch("addPurchaseCode");

  const handleAddToVoucher = () => {
    const productData = {
      relatedProduct: watch("relatedProduct"),
      relatedProductItem: watch("relatedProductItem"),
      relatedItemUnit: watch("relatedItemUnit"),
      qty: Number(watch("qty")),
      purchasePrice: Number(watch("purchasePrice")),
      expireDate: watch("expireDate") ?? null,
    };

    // Get the actual names from the data
    const selectedProduct = product?.data?.find(
      (p) => p._id === productData.relatedProduct,
    );
    const selectedProductItem = productItems?.data?.find(
      (item) => item._id === productData.relatedProductItem,
    );
    const selectedItemUnit = itemUnits?.data?.find(
      (unit) => unit._id === productData.relatedItemUnit,
    );

    // Check if ALL voucher items are exactly the same (including quantity)
    const hasExactDuplicate = selectedProductFields?.some(
      (voucherItem) =>
        voucherItem?.relatedProduct === productData?.relatedProduct &&
        voucherItem?.relatedProductItem === productData?.relatedProductItem &&
        voucherItem?.relatedItemUnit === productData?.relatedItemUnit &&
        voucherItem?.qty === productData?.qty &&
        voucherItem?.purchasePrice === productData?.purchasePrice &&
        voucherItem?.expiredDate === productData?.expireDate,
    );

    const subtotal = productData.qty * productData.purchasePrice || 0;

    if (!hasExactDuplicate) {
      appendVoucher({
        relatedProduct: productData.relatedProduct,
        relatedProductItem: productData.relatedProductItem,
        relatedItemUnit: productData.relatedItemUnit,
        productName: selectedProduct?.name || "",
        productItemName: selectedProductItem?.medicineItemName || "",
        unitName: selectedItemUnit?.name || "",
        qty: productData.qty,
        purchasePrice: productData.purchasePrice || 0,
        subtotal: subtotal,
        // expiredDate:
        //   productData.expireDate === "" ? null : productData.expireDate,
        expiredDate: productData?.expireDate,
      });
    } else {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "This voucher item already exists with the same product, product item, unit, quantity, price, and expire date",
        confirmButtonColor: "#d33",
        confirmButtonText: "OK",
      });
    }

    // Clear the product form after adding to voucher
    setValue("relatedProduct", "");
    setValue("relatedProductItem", "");
    setValue("relatedItemUnit", "");
    setValue("qty", "");
    setValue("purchasePrice", "");
    setValue("expireDate", "");

    setShowTable(true);
  };

  const handleRemoveVoucherItem = (index) => {
    removeVoucher(index);
    if (selectedProductFields.length === 1) {
      setShowTable(false);
    }
  };

  const calculateTotal = () => {
    return selectedProductFields.reduce(
      (total, item) => total + (item.subtotal || 0),
      0,
    );
  };

  const onSubmit = (data) => {
    if (selectedProductFields.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "At least one product is required",
        confirmButtonColor: "#d33",
        confirmButtonText: "OK",
      });
      return;
    }

    const payload = {
      ...data,
      total: calculateTotal(),
    };
    postData(payload);
  };

  useEffect(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const formattedDate = `${yyyy}-${mm}-${dd}`;

    reset({
      purchaseRequestDate: formattedDate,
    });
  }, []);

  useEffect(() => {
    const selectedItemUnit = itemUnits?.data?.find(
      (unit) => unit?._id === watchItemUnit,
    );

    if (selectedItemUnit) {
      setValue("qty", selectedItemUnit?.qty);
      setValue("purchasePrice", selectedItemUnit?.purchasePrice);
    }
  }, [watchItemUnit]);

  useEffect(() => {
    console.log("watchItemUnit", watchItemUnit);
    if (unitInfo?.data) {
      setValue("qty", unitInfo?.data?.qty);
      setValue("purchasePrice", unitInfo?.data?.purchasePrice);
    }
  }, [watchItemUnit,unitInfo?.data]);

  const handleChangeBtn = () => {
    updateData(watchItemUnit, { purchasePrice: watchPurchasePrice });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Purchase Request Form
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Global Error Display */}
        {Object.keys(errors).length > 0 && (
          <Grid item xs={12}>
            <Card
              sx={{
                mb: 3,
                bgcolor: "error.light",
                border: "1px solid",
                borderColor: "error.main",
              }}
            >
              <CardContent sx={{ py: 2 }}>
                <Typography variant="h6" color="white" gutterBottom>
                  Please fix the following errors:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2, color: "error.main" }}>
                  {errors.relatedBranch && (
                    <Typography component="li" color="white" variant="body2">
                      Branch is required
                    </Typography>
                  )}
                  {errors.relatedSupplier && (
                    <Typography component="li" color="white" variant="body2">
                      Supplier is required
                    </Typography>
                  )}
                  {selectedProductFields.length === 0 && (
                    <Typography component="li" color="white" variant="body2">
                      At least one product is required
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid container spacing={3}>
          {/* Left Column - Basic Information */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="relatedBranch"
                      control={control}
                      rules={{ required: "Branch is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <FormControl
                          fullWidth
                          variant="outlined"
                          error={!!error}
                        >
                          <InputLabel>Branch *</InputLabel>
                          <Select {...field} label="Branch *">
                            {!branchLoading &&
                              branchData?.data?.map((branch) => (
                                <MenuItem value={branch?._id} key={branch?._id}>
                                  {branch.name}
                                </MenuItem>
                              ))}
                          </Select>
                          {error && (
                            <FormHelperText>{error.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="relatedSupplier"
                      control={control}
                      rules={{ required: "Supplier is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <FormControl
                          fullWidth
                          variant="outlined"
                          error={!!error}
                        >
                          <InputLabel>Supplier *</InputLabel>
                          <Select {...field} label="Supplier *">
                            {!supplierLoading &&
                              suppliers?.data?.map((supplier) => (
                                <MenuItem
                                  value={supplier?._id}
                                  key={supplier?._id}
                                >
                                  {supplier?.name}
                                </MenuItem>
                              ))}
                          </Select>
                          {error && (
                            <FormHelperText>{error.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="purchaseRequestDate"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Purchase Request Date"
                          type="date"
                          fullWidth
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="requiredDate"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Required Date"
                          type="date"
                          fullWidth
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="reason"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Reason"
                          fullWidth
                          variant="outlined"
                          multiline
                          rows={5}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="remark"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Remark"
                          fullWidth
                          variant="outlined"
                          multiline
                          rows={5}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="addPurchaseCode"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox {...field} />}
                          label="Add Purchase Code"
                        />
                      )}
                    />
                  </Grid>
                  {watchAddPurchaseCode && (
                    <Grid item xs={12}>
                      <Controller
                        name="code"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Purchase Code"
                            fullWidth
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Products */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Products
                </Typography>
                <Grid container spacing={2} direction="column">
                  <Grid item>
                    <Controller
                      name="relatedProduct"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth variant="outlined" size="medium">
                          <InputLabel>Product</InputLabel>
                          <Select {...field} label="Product">
                            {!productLoading &&
                              product?.data?.map((el) => (
                                <MenuItem value={el?._id} key={el?._id}>
                                  {el?.name}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item>
                    <Controller
                      name="relatedProductItem"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          variant="outlined"
                          size="medium"
                          disabled={!watchProduct}
                        >
                          <InputLabel>Product Item</InputLabel>
                          <Select {...field} label="Product Item">
                            {!productItemLoading &&
                              productItems?.data?.map((item) => (
                                <MenuItem value={item?._id} key={item?._id}>
                                  {item?.medicineItemName}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item>
                    <Controller
                      name="relatedItemUnit"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          variant="outlined"
                          size="medium"
                          disabled={!watchProductItem}
                        >
                          <InputLabel>Item Unit</InputLabel>
                          <Select {...field} label="Item Unit">
                            {!itemUnitLoading &&
                              itemUnits?.data?.map((itemUnit) => (
                                <MenuItem
                                  key={itemUnit?._id}
                                  value={itemUnit?._id}
                                >
                                  {itemUnit?.name}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item>
                    <Controller
                      name="qty"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Quantity"
                          type="number"
                          fullWidth
                          variant="outlined"
                          size="medium"
                          disabled={!watchItemUnit}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item>
                    <Grid container spacing={1} alignItems="flex-end">
                      <Grid item xs={8}>
                        <Controller
                          name="purchasePrice"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Enter last Price"
                              type="number"
                              fullWidth
                              variant="outlined"
                              size="medium"
                              disabled={!watchItemUnit}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="medium"
                          type="button"
                          startIcon={<RefreshCcw size={16} />}
                          fullWidth
                          disabled={!watchItemUnit || updateLoading}
                          onClick={handleChangeBtn}
                        >
                          {updateLoading ? "Changing ..." : "Change"}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Controller
                      name="expireDate"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Expire Date"
                          type="date"
                          fullWidth
                          variant="outlined"
                          size="medium"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      startIcon={<AddIcon />}
                      onClick={handleAddToVoucher}
                      fullWidth
                      disabled={!watchItemUnit}
                    >
                      Add to Lists
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {showTable && (
          <Grid item xs={12}>
            <Card sx={{ mt: 3, mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Purchase Lists *
                </Typography>
                {selectedProductFields.length === 0 && (
                  <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                    At least one product is required
                  </Typography>
                )}
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Price</TableCell>
                        <TableCell>Subtotal</TableCell>
                        <TableCell>Expired Date</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedProductFields.map((field, index) => (
                        <TableRow key={field.id}>
                          <TableCell
                            sx={{
                              width: "300px",
                            }}
                          >
                            <div>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {field.productName}
                              </Typography>
                              <Box
                                sx={{
                                  mt: 1,
                                  display: "flex",
                                  gap: 1,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Chip
                                  label={field.productItemName}
                                  size="small"
                                  variant="filled"
                                  color="primary"
                                />
                                <Chip
                                  label={field.unitName}
                                  size="small"
                                  variant="filled"
                                  color="secondary"
                                />
                              </Box>
                            </div>
                          </TableCell>
                          {/* <TableCell>{field.productItemName}</TableCell>
                          <TableCell>{field.unitName}</TableCell> */}
                          <TableCell>
                            <TextField
                              value={field.qty}
                              onChange={(e) => {
                                const newQuantity = e.target.value;
                                const newSubtotal =
                                  newQuantity * field.purchasePrice || 0;
                                updateVoucher(index, {
                                  ...field,
                                  qty: newQuantity,
                                  subtotal: newSubtotal,
                                });
                              }}
                              type="number"
                              size="small"
                              variant="outlined"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={field.purchasePrice}
                              onChange={(e) => {
                                const newPrice = e.target.value;
                                const newSubtotal = field.qty * newPrice || 0;
                                updateVoucher(index, {
                                  ...field,
                                  purchasePrice: newPrice,
                                  subtotal: newSubtotal,
                                });
                              }}
                              type="number"
                              size="small"
                              variant="outlined"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>{field.subtotal}</TableCell>
                          <TableCell>
                            <TextField
                              value={field.expiredDate}
                              onChange={(e) => {
                                const newExpiredDate = e.target.value;
                                updateVoucher(index, {
                                  ...field,
                                  expiredDate: newExpiredDate,
                                });
                              }}
                              type="date"
                              size="small"
                              variant="outlined"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveVoucherItem(index)}
                              size="small"
                            >
                              <CircleMinus size={20} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={6} align="right">
                          <Typography variant="subtitle1" fontWeight="bold">
                            Total:
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {calculateTotal()}
                          </Typography>
                        </TableCell>
                        <TableCell colSpan={1}></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              size="large"
              disabled={loading}
            >
              {loading ? "Saving ..." : "Save Purchase Request"}
            </Button>
          </Box>
        </Grid>
      </form>
    </Box>
  );
};

export default PurchaseForm;
